import { type StoreObject, useMutation } from "@apollo/client";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import CloudinaryImage from "@/components/cloudinary-image";
import ProfileCard from "@/components/profile-card";
import { Button } from "@/components/ui/button";
import UserSearchModal, { type UserType } from "@/components/user-search-modal";
import useLeaveEscape from "@/graphql/mutations/use-leave-escape";
import useUpdateEscapeMutation, {
  convertEscapeToInput,
  type EscapeType,
  type ExperienceReviewInputType,
  type TaggedUserType,
} from "@/graphql/mutations/use-update-escape";
import useVerifyUserEscapeTag, {
  type EscapeType as VerifyUserEscapeType,
} from "@/graphql/queries/use-verify-user-escape-tag";
import { cache } from "@/helpers/apollo";
import useAuth from "@/hooks/use-auth";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";
import { JOIN_ESCAPE_MUTATION } from "@/pages/experience/graphql";
import { escapeToInput } from "@/pages/experience/helpers";
import JoinModal from "@/pages/experience/team/join-modal";
import type {
  JoinEscapeDataType,
  JoinEscapeVariablesType,
} from "@/pages/experience/types";

import type { UserGameType } from "../types";
import { PlusIcon } from "./icons";

interface TaggedPlayerProps {
  user: TaggedUserType;
}

function TaggedPlayer({ user }: TaggedPlayerProps) {
  const isDesktop = useIsBreakPoint("md");

  return (
    <ProfileCard userId={user.id}>
      <Link target={isDesktop ? "_blank" : undefined} to={`/@${user.username}`}>
        <CloudinaryImage
          className="size-[45px] rounded-full"
          rW={90}
          size={45}
          user={user}
        />
      </Link>
    </ProfileCard>
  );
}

interface TagPlayersProps {
  game: UserGameType;
}

export default function TagPlayers({ game }: TagPlayersProps) {
  const [joinProps, setJoinProps] = useState<{
    escape: VerifyUserEscapeType;
    user: UserType;
  } | null>(null);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [isUsersSearchModalOpen, setIsUsersSearchModalOpen] = useState(false);

  const { me, mutationContext } = useAuth();

  const [updateEscape] = useUpdateEscapeMutation();
  const [joinEscape] = useMutation<JoinEscapeDataType, JoinEscapeVariablesType>(
    JOIN_ESCAPE_MUTATION
  );
  const [leaveEscape] = useLeaveEscape();
  const verifyUserEscapeTag = useVerifyUserEscapeTag();

  const escapeObj = game.myEscape as EscapeType;

  const joinEscapeCb = async (
    escapeId: string,
    experienceReview?: ExperienceReviewInputType
  ) => {
    const { data: joinData } = await joinEscape(mutationContext({ escapeId }));

    let newEscape = joinData?.joinEscape.escape as unknown as
      | EscapeType
      | undefined;

    if (!!joinData?.joinEscape.escape && experienceReview) {
      const { escapeInput, taggedUsers } = escapeToInput(
        joinData.joinEscape.escape,
        game.id,
        me as UserType
      );

      const { data: updateData } = await updateEscape(
        mutationContext({
          escape: {
            ...escapeInput,
            taggedUsers: taggedUsers.map(({ id }) => id),
          },
          experienceReview: {
            ...experienceReview,
            images: [],
          },
        })
      );

      if (updateData?.updateEscape.escape) {
        newEscape = updateData.updateEscape.escape;
      }
    }

    cache.modify({
      fields: {
        isCompleted: () => !!newEscape,
        myEscape: () => newEscape ?? null,
      },
      id: cache.identify(game as unknown as unknown as StoreObject),
    });
  };

  const handleJoinEscape = async (escapeId: string) => {
    if (escapeObj.id) {
      const { experienceReview } = convertEscapeToInput(escapeObj);
      const { data } = await leaveEscape(mutationContext({ id: escapeObj.id }));

      if (data?.leaveEscape.removed) {
        joinEscapeCb(escapeId, experienceReview);
      }
    } else {
      joinEscapeCb(escapeId);
    }

    setIsJoinModalOpen(false);
    setIsUsersSearchModalOpen(false);
  };

  const handleUserSelect = (checkUser: UserType) =>
    new Promise<UserType>((resolve, reject) => {
      if (!escapeObj.game) {
        resolve(checkUser);
        return;
      }

      (async () => {
        const { data } = await verifyUserEscapeTag({
          gameId: escapeObj.game?.id || "",
          userId: checkUser.id,
        });

        const { escape: verifiedEscape, user } = data.verifyUserEscapeTag;

        if (verifiedEscape) {
          setJoinProps({ escape: verifiedEscape, user });
          setIsJoinModalOpen(true);
          reject();
        } else {
          resolve(data.verifyUserEscapeTag.user);
        }
      })();
    });

  const handleUsersSubmit = async (taggedUsers: UserType[]) => {
    setIsUsersSearchModalOpen(false);
    const newEscape = await updateEscape(
      mutationContext(convertEscapeToInput({ ...escapeObj, taggedUsers }))
    );
    cache.modify({
      fields: {
        myEscape: () => newEscape.data?.updateEscape.escape,
      },
      id: cache.identify(game as unknown as unknown as StoreObject),
    });
  };

  const USER_ICONS_COUNT = 4;

  const { taggedUsers, userIcons } = useMemo(() => {
    if (!me) {
      return { taggedUsers: [], userIcons: [] };
    }
    if (!escapeObj.taggedUsers.length) {
      return {
        taggedUsers: [me],
        userIcons: [<TaggedPlayer key={me.id} user={me} />],
      };
    }

    return {
      taggedUsers: escapeObj.taggedUsers,
      userIcons: escapeObj.taggedUsers.map((taggedUser) => (
        <TaggedPlayer key={taggedUser.id} user={taggedUser} />
      )),
    };
  }, [me, escapeObj.taggedUsers]);

  if (!me) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-2.5">
        {userIcons.slice(0, USER_ICONS_COUNT)}
        {userIcons.length > USER_ICONS_COUNT && (
          <p className="size-[45px] rounded-full bg-white/20 text-center font-medium text-16/[45px] text-white">
            +{userIcons.length - USER_ICONS_COUNT}
          </p>
        )}
        <Button
          aria-label="Tag people"
          className="flex size-[45px] rounded-full border border-white/20 border-dashed font-medium text-16 hover:bg-white/10 md:w-auto md:flex-row-reverse md:rounded-[23px] md:px-5"
          onClick={() => setIsUsersSearchModalOpen(true)}
        >
          <PlusIcon />
          <span className="hidden md:inline">Tag people</span>
        </Button>
      </div>
      <UserSearchModal
        onOpenChange={setIsUsersSearchModalOpen}
        onSelect={handleUserSelect}
        onSubmit={handleUsersSubmit}
        open={isUsersSearchModalOpen}
        readonlyUsers={[me.id]}
        title="Tag people"
        users={taggedUsers}
      />
      {!!joinProps && (
        <JoinModal
          onContinue={handleJoinEscape}
          onOpenChange={setIsJoinModalOpen}
          open={isJoinModalOpen}
          {...joinProps}
        />
      )}
    </>
  );
}
